import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Pencil, Sparkles, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { LETTER_TEMPLATES } from "../lib/letterTemplates";
import { fillTemplate, toTemplate } from "../lib/decisionTemplates";
import { SaveDecisionTplDialog } from "./SaveDecisionTplDialog";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const ph = (k, v) => `<span data-ph="${k}">${v}</span>`;
const unPh = (h) => h.replace(/<span data-ph="(\w+)">[\s\S]*?<\/span>/g, "{{$1}}");
const FEMALE = [["السيد/", "السيدة/"], ["يعمل لدينا", "تعمل لدينا"], ["قد عمل لدينا", "قد عملت لدينا"], ["ولا يزال", "ولا تزال"], ["ويتقاضى", "وتتقاضى"],
  ["بناءً على طلبه", "بناءً على طلبها"], ["أُعطيت له", "أُعطيت لها"], ["له بالتوفيق", "لها بالتوفيق"], ["وقد كان خلال فترة عمله", "وقد كانت خلال فترة عملها"]];
const isF = (emp) => /انث|أنث|female/i.test(emp?.gender || "");
const masculine = (h, emp) => (isF(emp) ? FEMALE.reduce((x, [m, f]) => x.split(f).join(m), h) : h);
const gendered = (h, emp) => (/انث|أنث|female/i.test(emp?.gender || "") ? FEMALE.reduce((x, [m, f]) => x.split(m).join(f), h) : h);

// Ready + company-saved letter templates, auto-filled from the selected employee
export const LetterTemplates = ({ ed, emp, ctx, onTitle }) => {
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.decisionTemplates("letter").then(setMine).catch(() => {}); }, []);
  const extra = async () => {
    const x = { company: ctx.company, date: ctx.date };
    if (!emp) return x;
    const s = await api.letterEmpSalary(emp.id).catch(() => null);
    return s ? { ...x, salary_basic: ph("salary_basic", fmt(s.basic)), salary_housing: ph("salary_housing", fmt(s.housing)), salary_transport: ph("salary_transport", fmt(s.transport)),
      salary_other: ph("salary_other", fmt(s.other)), salary_total: ph("salary_total", fmt(s.total)) } : x;
  };
  const apply = async (tp) => {
    if (!emp) toast.info("لم تختر موظفاً — ستظهر البيانات كفراغات لتكملها");
    ed.current?.setHTML(gendered(fillTemplate(tp.body ?? tp.body_html ?? "", emp, await extra()), emp));
    onTitle(tp.title); toast.success(`تمت تعبئة «${tp.title}»`);
  };
  const save = async (title, existing) => {
    const body = { kind: "letter", type: "أخرى", title, body_html: toTemplate(masculine(unPh(ed.current?.getHTML() || ""), emp), emp, { company: ctx.company, date: ctx.date }) };
    try {
      const r = existing ? await api.updateDecisionTemplate(existing.id, body) : await api.createDecisionTemplate(body);
      setMine((p) => (existing ? p.map((t) => (t.id === r.id ? r : t)) : [...p, r])); setEditing(null);
      toast.success(existing ? "تم تحديث القالب" : "تم حفظ القالب ✅");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر حفظ القالب"); throw e; }
  };
  const del = async (t) => {
    if (!window.confirm(`حذف القالب «${t.title}»؟`)) return;
    try { await api.deleteDecisionTemplate(t.id); setMine((p) => p.filter((x) => x.id !== t.id)); if (editing?.id === t.id) setEditing(null); toast.success("تم حذف القالب"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-2.5" data-testid="letter-templates">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-blue-800"><Sparkles className="h-3.5 w-3.5" /> قوالب جاهزة (تعبئة تلقائية)</p>
      <div className="flex flex-wrap gap-1.5">
        {LETTER_TEMPLATES.map((tp) => (
          <button key={tp.key} type="button" onClick={() => apply(tp)} data-testid={`letter-tpl-${tp.key}`}
            className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-700 transition-colors hover:bg-blue-600 hover:text-white">{tp.title}</button>))}
        {mine.map((tp) => (
          <span key={tp.id} className={`inline-flex items-center gap-0.5 rounded-full border px-1 py-0.5 ${editing?.id === tp.id ? "border-amber-500 bg-amber-100" : "border-amber-300 bg-amber-50"}`} data-testid={`letter-mytpl-${tp.id}`}>
            <button type="button" onClick={() => apply(tp)} data-testid={`letter-mytpl-apply-${tp.id}`} className="rounded-full px-1.5 text-[11px] font-semibold text-amber-800">{tp.title}</button>
            <button type="button" title="تعديل" onClick={() => { apply(tp); setEditing(tp); toast.info("عدّل النص ثم اضغط «حفظ كقالب» واختر تحديث"); }} data-testid={`letter-mytpl-edit-${tp.id}`} className="rounded-full p-0.5 text-amber-700 hover:bg-amber-200"><Pencil className="h-3 w-3" /></button>
            <button type="button" title="حذف" onClick={() => del(tp)} data-testid={`letter-mytpl-delete-${tp.id}`} className="rounded-full p-0.5 text-red-600 hover:bg-red-100"><Trash2 className="h-3 w-3" /></button>
          </span>))}
      </div>
      <button type="button" onClick={() => setOpen(true)} data-testid="letter-save-as-template"
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-amber-400 bg-white py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-50">
        <BookmarkPlus className="h-3.5 w-3.5" /> {editing ? `حفظ / تحديث «${editing.title}»` : "حفظ كقالب"}</button>
      <SaveDecisionTplDialog open={open} onOpenChange={setOpen} defaultTitle={ctx.title} editing={editing} onSave={save} />
    </div>
  );
};
